import DocumentPage from './DocumentPage';

export const timetableDocument = {
  slug: 'timetable',
  route: '/timetable',
  title: 'Time Table',
  category: 'Academics',
  format: 'DOCX',
  description:
    'EduX provides an integrated timetable workflow for session planning, generation, reporting, attendance visibility, and faculty-wise session tracking.',
  metrics: [
    { label: 'Primary focus', value: 'Session planning' },
    { label: 'Core records', value: 'Generated sessions' },
    { label: 'Coverage', value: 'Planning to faculty view' },
  ],
  workflow: [
    'Open Time Table to review the session planning calendar and current scheduled sessions.',
    'Use Configuration and Generate Time Table to define the course, batch, date range, and day-wise session details.',
    'Review the generated session report, attendance sheet, and faculty-wise sessions for operational follow-up.',
  ],
  sections: [
    {
      id: 'time-table-overview',
      title: 'Time Table Overview',
      body: [
        'EduX is a fully integrated application that provides a facility to see the session planning. Go to Time Table to access the weekly and day-wise planning view.',
        'This screen gives a visual session calendar so teams can quickly understand when a faculty member, subject, or classroom is already allocated.',
      ],
      figure: {
        src: '/assets/doc-images/timetable/image2.png',
        alt: 'Time Table weekly session planning view',
        caption: 'Time Table screen showing the session planning calendar and the scheduled slot.',
      },
      bullets: [
        'Use the calendar view to review session planning week by week.',
        'Check the scheduled session block directly from the planning screen.',
        'Go to Time Table -> Configuration -> Generate Time Table to create new timetable sessions.',
      ],
    },
    {
      id: 'generate-sessions',
      title: 'Generate Sessions',
      body: [
        'The Generate Sessions window is used to create timetable entries from the selected academic setup.',
        'Under this basic information of the timetable, you can provide the details of the timetable day wise. It consists of several pieces of information.',
      ],
      figure: {
        src: '/assets/doc-images/timetable/image3.png',
        alt: 'Generate Sessions form with course batch and day wise inputs',
        caption: 'Generate Sessions form where the timetable is created for the selected date range.',
      },
      detailCards: [
        {
          title: 'Details of Generate Sessions',
          bullets: [
            'Course: select the course from the list.',
            'Batch: select the batch from the list.',
            'Start Date: specify the starting date of the session.',
            'End Date: specify the ending date of the session.',
          ],
        },
        {
          title: 'Day-wise timetable details',
          bullets: [
            'Day: activate the day of the week for the session.',
            'Timing: select the timing of the session.',
            'Faculty: select the faculty to which the session is allocated.',
            'Subject: select the subject held by the faculty.',
            'Classroom: select the classroom in which the session will be held.',
          ],
        },
      ],
      steps: [
        'Open Generate Time Table from the Configuration area.',
        'Choose the course, batch, start date, and end date.',
        'Add the day-wise line items with time, faculty, subject, and classroom.',
        'Proceed to generate the timetable sessions.',
      ],
    },
    {
      id: 'reports-and-attendance',
      title: 'Reports and Attendance',
      body: [
        'After sessions are generated, the timetable workflow also supports reporting and attendance visibility from the same operational flow.',
        'These options help users review the completed timetable output and the attendance structure connected to a session.',
      ],
      subsections: [
        {
          id: 'generate-session-report',
          title: 'Generate Session Report',
          body: [
            'The Generate Session Report option allows users to download or view the complete timetable details.',
          ],
          figure: {
            src: '/assets/doc-images/timetable/image3.png',
            alt: 'Generate Session Report source screen from timetable generation',
            caption: 'The generated timetable can be reviewed from the same session creation flow.',
          },
        },
        {
          id: 'attendance-sheet',
          title: 'Attendance Sheet',
          body: [
            'You can find the details of student attendance from this button.',
            'Here, we can see the day-wise timetable as shown in the image, along with the register, session, course, batch, and attendance rows.',
          ],
          figure: {
            src: '/assets/doc-images/timetable/image4.png',
            alt: 'Attendance sheet connected to a generated timetable session',
            caption: 'Attendance Sheet view linked to the timetable session.',
          },
        },
      ],
    },
    {
      id: 'faculty-sessions',
      title: 'Faculty Sessions',
      body: [
        'A Sessions smart button is available in the Faculty form.',
        'When clicked, it redirects to the list of sessions assigned to that particular faculty and provides a faculty-wise view of the timetable.',
      ],
      figure: {
        src: '/assets/doc-images/timetable/image5.png',
        alt: 'Faculty wise list of assigned timetable sessions',
        caption: 'Faculty-wise session list showing the day, time, course, faculty, and batch.',
      },
      bullets: [
        'Open the Faculty form and use the Sessions smart button.',
        'Review the sessions assigned to that faculty member only.',
        'Use the session list as a quick faculty-wise timetable view.',
      ],
      subsections: [
        {
          id: 'faculty-session-details',
          title: 'Faculty Session Details',
          body: [
            'The faculty session detail page displays the core information of the selected timetable entry.',
            'It displays details such as Subject, Batch, Classroom, Date, and Session Time.',
          ],
          bullets: [
            'Subject',
            'Batch',
            'Classroom',
            'Date',
            'Session Time',
          ],
          figure: {
            src: '/assets/doc-images/timetable/image6.png',
            alt: 'Detailed faculty session record for a timetable entry',
            caption: 'Detailed faculty session view with course, subject, classroom, batch, and session time.',
          },
        },
      ],
    },
  ],
  related: ['attendance', 'faculty', 'student'],
};

export default function TimetablePage() {
  return <DocumentPage document={timetableDocument} />;
}
